import React, { useState } from "react";
import {
  Download, Share2, ShieldCheck, AlertOctagon, Link2, ListTree, Quote, Lightbulb, Plus,
  Brain, Zap, CheckCircle, AlertTriangle,
} from "lucide-react";
import ScoreBar from "./ScoreGauge";
import {
  VERDICT_META, SCORE_DIMENSIONS, credBadge, formatRelative, downloadJSON, prettify,
} from "../../lib/format";

const TABS = [
  { id: "overview", label: "Scores", icon: ShieldCheck },
  { id: "claims", label: "Claims & flags", icon: AlertOctagon },
  { id: "sources", label: "Sources", icon: Link2 },
  { id: "timeline", label: "Timeline", icon: ListTree },
  { id: "intelligence", label: "Intelligence", icon: Brain },
];

export default function ResultsDashboard({ result, onReset }) {
  const [tab, setTab] = useState("overview");
  if (!result) return null;
  
  // Ensure result has all required fields with defaults
  const safeResult = {
    verdict: "unverifiable",
    title: "Analysis Results",
    summary: "No summary available",
    confidence: 0,
    model_label: "Unknown",
    created_at: new Date().toISOString(),
    content_type: "text",
    scores: {
      truth: 50,
      bias: 50,
      source_credibility: 50,
      sentiment: 50,
      objectivity: 50,
      source_authority: 50,
      cross_platform_consistency: 50,
      semantic_consistency: 50,
    },
    sentiment_label: "neutral",
    bias_direction: "unknown",
    sources: [],
    red_flags: [],
    key_claims: [],
    timeline: [],
    recommendations: [],
    intelligence_score: 50,
    ...result,
  };
  
  const v = VERDICT_META[safeResult.verdict] || VERDICT_META.unverifiable;

  const handleShare = async () => {
    const text = `Nayan.AI verdict: ${v.label} (${safeResult.confidence}% confidence), ${safeResult.title}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Nayan.AI Analysis", text });
      } else {
        await navigator.clipboard.writeText(text);
      }
    } catch {}
  };

  return (
    <div data-testid="results-dashboard" className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-500">
      {/* Verdict */}
      <div
        data-testid="verdict-card"
        className="relative rounded-2xl border bg-ink-surface/70 backdrop-blur p-7 md:p-9 overflow-hidden shadow-2xl shadow-black/30"
        style={{ borderColor: v.border }}
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{ background: `radial-gradient(ellipse 80% 100% at 0% 0%, ${v.bg}, transparent 70%)` }}
        />

        <div className="relative flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
              <span className="px-2 py-1 rounded-full bg-brand/10 border border-brand/20 text-brand font-medium">N.E.T.R.A Intelligence</span>
              <span className="text-zinc-700">·</span>
              <span data-testid="result-time">{formatRelative(safeResult.created_at)}</span>
              <span className="text-zinc-700">·</span>
              <span className="capitalize px-2 py-1 rounded-full bg-ink-elevated/60 border border-ink-border-subtle">{safeResult.content_type} input</span>
            </div>

            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <span
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shadow-lg"
                style={{ backgroundColor: v.bg, color: v.color, borderColor: v.border, borderWidth: 1 }}
                data-testid="verdict-badge"
              >
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: v.color }} />
                {v.label}
              </span>
              <span className="text-sm text-zinc-400 font-medium">{v.short}</span>
            </div>

            <h2
              data-testid="result-title"
              className="mt-5 text-white text-3xl md:text-4xl font-bold tracking-tight leading-tight"
            >
              {safeResult.title}
            </h2>

            <p className="mt-4 max-w-3xl text-zinc-300 text-[15px] leading-relaxed">
              {safeResult.summary}
            </p>

            <div className="mt-6 flex items-center gap-6">
              <div>
                <div className="text-xs text-zinc-500 font-medium uppercase tracking-wide">Confidence</div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="w-40 h-2 rounded-full bg-ink-soft overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-1000 ease-out shadow-lg"
                      style={{ width: `${safeResult.confidence}%`, backgroundColor: v.color }}
                    />
                  </div>
                  <span
                    className="text-lg font-bold tabular-nums"
                    style={{ color: v.color }}
                    data-testid="confidence-value"
                  >
                    {safeResult.confidence}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 shrink-0">
            <button
              onClick={onReset}
              data-testid="new-analysis-button"
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-100 px-5 py-2.5 rounded-full font-semibold text-sm transition-all shadow-lg hover:shadow-xl"
            >
              <Plus size={16} /> New analysis
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => downloadJSON(safeResult, `nayan-${safeResult.id.slice(0, 8)}.json`)}
                data-testid="export-json-button"
                className="inline-flex items-center gap-2 text-zinc-300 hover:text-white border border-ink-border-subtle hover:border-zinc-600 hover:bg-ink-elevated/60 px-4 py-2.5 rounded-full text-sm transition-all"
                title="Download JSON"
              >
                <Download size={15} /> Export
              </button>
              <button
                onClick={handleShare}
                data-testid="share-button"
                className="inline-flex items-center gap-2 text-zinc-300 hover:text-white border border-ink-border-subtle hover:border-zinc-600 hover:bg-ink-elevated/60 px-4 py-2.5 rounded-full text-sm transition-all"
                title="Share"
              >
                <Share2 size={15} /> Share
              </button>
            </div>
          </div>
        </div>

        {/* meta strip */}
        <div className="relative mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Chip label="Tone" value={prettify(safeResult.sentiment_label)} />
          <Chip label="Bias direction" value={prettify(safeResult.bias_direction)} />
          <Chip label="Sources cited" value={String(safeResult.sources?.length || 0)} />
          <Chip label="Red flags" value={String(safeResult.red_flags?.length || 0)} accent={safeResult.red_flags?.length ? v.color : undefined} />
        </div>

        {/* Description of verdict */}
        <p className="relative mt-6 text-sm text-zinc-400 max-w-3xl leading-relaxed border-l-3 pl-4 py-2 bg-ink-elevated/30 rounded-r-lg" style={{ borderColor: v.color }}>
          <span className="text-zinc-300 font-semibold">What this means: </span>
          {v.desc}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1.5 rounded-2xl border border-ink-border-subtle bg-ink-surface/60 backdrop-blur w-fit overflow-x-auto max-w-full shadow-lg">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`results-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                active ? "bg-white text-black shadow-lg" : "text-zinc-400 hover:text-white hover:bg-ink-elevated/60"
              }`}
            >
              <Icon size={15} strokeWidth={2} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="mt-6">
        {tab === "overview" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
            {SCORE_DIMENSIONS.map((d, i) => (
              <ScoreBar
                key={d.key}
                value={safeResult.scores?.[d.key] ?? 50}
                dimension={d}
                testId={`score-${d.key}`}
                delay={i * 0.05}
              />
            ))}
            {safeResult.recommendations?.length > 0 && (
              <div className="col-span-1 sm:col-span-2 rounded-xl border border-brand/30 bg-brand/5 p-6">
                <div className="flex items-center gap-2 text-sm text-brand font-medium">
                  <Lightbulb size={14} /> What to do with this
                </div>
                <ul className="mt-3 space-y-2.5 text-zinc-200" data-testid="recommendations-list">
                  {safeResult.recommendations.map((r, i) => (
                    <li key={i} className="flex gap-3 text-[15px] leading-relaxed">
                      <span className="text-brand tabular-nums shrink-0 mt-0.5">{i + 1}.</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {tab === "claims" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PanelList
              title="Key claims"
              icon={Quote}
              items={safeResult.key_claims}
              empty="No specific claims were extracted."
              testId="key-claims"
              accent="#8fb8e0"
            />
            <PanelList
              title="Red flags"
              icon={AlertOctagon}
              items={safeResult.red_flags}
              empty="No red flags were detected."
              testId="red-flags"
              accent="#f08a8a"
            />
          </div>
        )}

        {tab === "sources" && (
          <div className="rounded-xl border border-ink-border-subtle bg-ink-surface/60 overflow-hidden" data-testid="sources-table">
            {(safeResult.sources || []).length === 0 ? (
              <div className="px-6 py-10 text-zinc-500 text-sm text-center">
                No sources were cited in this content.
              </div>
            ) : (
              <ul className="divide-y divide-ink-border-subtle">
                {safeResult.sources.map((s, i) => {
                  const b = credBadge(s.credibility);
                  return (
                    <li key={i} className="px-6 py-4 hover:bg-ink-elevated/40 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          {s.url ? (
                            <a
                              href={s.url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-white hover:text-brand underline-offset-4 hover:underline break-all font-medium"
                            >
                              {s.name}
                            </a>
                          ) : (
                            <span className="text-white font-medium">{s.name}</span>
                          )}
                          {s.note && (
                            <div className="mt-1 text-sm text-zinc-400 leading-relaxed">{s.note}</div>
                          )}
                        </div>
                        <span
                          className="shrink-0 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border"
                          style={{ borderColor: b.color + "55", color: b.color, backgroundColor: b.color + "10" }}
                        >
                          <span className="w-1 h-1 rounded-full" style={{ backgroundColor: b.color }} />
                          {b.label}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {tab === "timeline" && (
          <div className="relative pl-8 ml-2" data-testid="timeline-list">
            <div className="absolute left-2 top-0 bottom-0 w-px bg-ink-border-subtle" />
            {(safeResult.timeline || []).length === 0 ? (
              <div className="text-zinc-500 text-sm">No timeline data was inferred.</div>
            ) : (
              safeResult.timeline.map((t, i) => (
                <div key={i} className="relative mb-7 last:mb-0">
                  <span className="absolute -left-[28px] top-1.5 w-3 h-3 rounded-full bg-brand ring-4 ring-ink-base" />
                  <div className="text-sm text-brand font-medium">{t.label}</div>
                  <div className="mt-1 text-zinc-200 leading-relaxed">{t.description}</div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "intelligence" && (
          <div className="space-y-6" data-testid="intelligence-tab">
            {/* Intelligence Score */}
            <div className="rounded-xl border border-ink-border-subtle bg-ink-surface/60 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Brain size={20} className="text-brand" />
                <h3 className="text-white font-semibold">Intelligence Score</h3>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-5xl font-bold text-brand tabular-nums">
                  {safeResult.intelligence_score || 50}
                </div>
                <div className="text-sm text-zinc-400">
                  Overall analysis quality (0-100)
                </div>
              </div>
              {safeResult.cache_hit && (
                <div className="mt-4 flex items-center gap-2 text-sm text-green-400">
                  <Zap size={14} />
                  <span>Result retrieved from cache</span>
                </div>
              )}
            </div>

            {/* Quality Indicators */}
            {safeResult.quality_indicators && Object.keys(safeResult.quality_indicators).length > 0 && (
              <div className="rounded-xl border border-ink-border-subtle bg-ink-surface/60 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle size={20} className="text-brand" />
                  <h3 className="text-white font-semibold">Quality Indicators</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(safeResult.quality_indicators).map(([key, value]) => (
                    <div key={key} className="text-center">
                      <div className="text-[11px] text-zinc-500 uppercase">{key}</div>
                      <div className="mt-1 font-medium text-white">{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Synthesized Insights */}
            {safeResult.synthesized_insights && safeResult.synthesized_insights.length > 0 && (
              <div className="rounded-xl border border-ink-border-subtle bg-ink-surface/60 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Lightbulb size={20} className="text-brand" />
                  <h3 className="text-white font-semibold">Synthesized Insights</h3>
                </div>
                <ul className="space-y-3">
                  {safeResult.synthesized_insights.map((insight, i) => (
                    <li key={i} className="flex gap-3 text-zinc-200 text-sm leading-relaxed">
                      <span className="text-brand mt-1">•</span>
                      <span>{insight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Model Selection Reason */}
            {safeResult.model_selection_reason && (
              <div className="rounded-xl border border-ink-border-subtle bg-ink-surface/60 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Zap size={20} className="text-brand" />
                  <h3 className="text-white font-semibold">Model Selection</h3>
                </div>
                <p className="text-sm text-zinc-300">{safeResult.model_selection_reason}</p>
              </div>
            )}

            {/* Validation Issues */}
            {safeResult.validation_issues && safeResult.validation_issues.length > 0 && (
              <div className="rounded-xl border border-red-900/30 bg-red-950/20 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle size={20} className="text-red-400" />
                  <h3 className="text-white font-semibold">Validation Issues</h3>
                </div>
                <ul className="space-y-2">
                  {safeResult.validation_issues.map((issue, i) => (
                    <li key={i} className="text-sm">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs mr-2 ${
                        issue.severity === "critical" ? "bg-red-500/20 text-red-400" :
                        issue.severity === "warning" ? "bg-yellow-500/20 text-yellow-400" :
                        "bg-zinc-500/20 text-zinc-400"
                      }`}>
                        {issue.severity}
                      </span>
                      <span className="text-zinc-300">{issue.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Chip({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-ink-elevated/60 border border-ink-border-subtle px-4 py-3.5 hover:bg-ink-elevated/80 transition-colors">
      <div className="text-[11px] text-zinc-500 font-medium uppercase tracking-wide">{label}</div>
      <div className="mt-1 font-semibold text-[15px]" style={{ color: accent || "#fff" }}>
        {value}
      </div>
    </div>
  );
}

function PanelList({ title, icon: Icon, items = [], empty, testId, accent }) {
  return (
    <div className="rounded-xl border border-ink-border-subtle bg-ink-surface/60 overflow-hidden shadow-lg" data-testid={testId}>
      <div className="flex items-center gap-2 px-5 py-4 border-b border-ink-border-subtle bg-ink-elevated/30">
        <Icon size={16} style={{ color: accent }} />
        <span className="text-white font-semibold">{title}</span>
        <span className="ml-auto text-xs text-zinc-500 font-medium px-2 py-1 rounded-full bg-ink-soft">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-10 text-zinc-500 text-sm text-center">{empty}</div>
      ) : (
        <ul className="divide-y divide-ink-border-subtle">
          {items.map((it, i) => (
            <li key={i} className="px-5 py-4 flex gap-3 text-zinc-200 leading-relaxed hover:bg-ink-elevated/40 transition-colors">
              <span
                className="shrink-0 mt-1.5 w-2 h-2 rounded-full"
                style={{ backgroundColor: accent }}
              />
              <span className="text-[15px]">{it}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
